import { OpenInNew } from '@mui/icons-material'
import {
  IconButton,
  InputAdornment,
  OutlinedTextFieldProps,
  TextField,
  Tooltip
} from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { IMaskInput } from 'react-imask'

// MUI's inputComponent interface requires `ref` to be called with HTMLInputElement.
// IMaskInput uses `inputRef` for DOM access, so we need this adapter.
const MaskedInputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<typeof IMaskInput>
>(function MaskedInputComponent(props, ref) {
  return <IMaskInput {...props} inputRef={ref} />
})
import { formatPhoneNumberIntl } from 'react-phone-number-input'

import colors from '../../../theme/colors'
import CopyAddressButton from '../../molecules/CopyAddressButton/CopyAddressButton'
import CopyButton from '../CopyButton'
import MailTo from '../MailTo'
import { CustomNumber, PhoneNumberInput } from './components'

export interface InputProps extends OutlinedTextFieldProps {
  disabled?: boolean
  fullWidth?: boolean
  label?: string
  multiline?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  rows?: number
  showPassword?: boolean
  small?: boolean
  type?:
    | 'default'
    | 'money'
    | 'password'
    | 'textfield'
    | 'phone'
    | 'email'
    | 'copyAddress'
    | 'copyable'
    | 'externalLink'
  value?: string
  defaultValue?: string
  name?: string
  error?: any
  autoFocus?: boolean
  helperText?: string
  helperTextColor?: string | undefined
  noPlaceholder?: boolean
  thickBorder?: boolean
  subject?: string
  body?: string
  prefix?: string
  bcc?: string
  externalUrl?: string
  mask?: string
  maskChar?: string[0]
  alwaysShowMask?: boolean
  permanents?: number[]
  caseEmailContext?: { caseId: string; processingId?: string }
}
type InputPropsType = Omit<InputProps, 'variant'>

const Input = React.forwardRef<FC, InputPropsType>(
  (
    {
      thickBorder,
      disabled = false,
      fullWidth = false,
      label = '',
      multiline = false,
      onChange,
      onBlur,
      rows,
      showPassword = false,
      small = true,
      type = 'default',
      value = undefined,
      defaultValue = undefined,
      name,
      error = false,
      autoFocus = false,
      helperText = undefined,
      helperTextColor,
      noPlaceholder = false,
      mask,
      maskChar,
      alwaysShowMask,
      permanents,
      externalUrl,
      caseEmailContext,
      ...props
    },
    ref
  ) => {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
      setVisible(!!showPassword)
    }, [showPassword])

    interface IDefaultFieldProps {
      variant: 'outlined'
      disabled?: boolean
      autoFocus?: boolean
      label?: string
      name?: string
      onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
      onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
      size?: 'small' | 'medium'
      value?: string
      defaultValue?: string
      fullWidth?: boolean
      multiline?: boolean
      rows?: number
      error?: any
      helperText?: string
      thickBorder?: boolean
      subject?: string
      body?: string
      bcc?: string
      prefix?: string
      caseEmailContext?: { caseId: string; processingId?: string }
    }

    const defaultFieldProps = {
      ...props,
      variant: 'outlined',
      disabled,
      autoFocus,
      label,
      name,
      onChange,
      onBlur,
      size: small ? 'small' : 'medium',
      value,
      defaultValue,
      fullWidth,
      multiline,
      rows,
      slotProps: {},
      error: !!error,
      helperText,
      ...(thickBorder
        ? {
            sx: {
              '& .MuiOutlinedInput-notchedOutline': {
                border: `5px solid ${colors.petrol} !important`
              },
              '& .Mui-focused > .MuiOutlinedInput-notchedOutline ': {
                border: '2px solid #3b82f6 !important'
              }
            }
          }
        : {})
    } as IDefaultFieldProps

    // Define new slotProps for different types

    // MONEY: use CustomNumber for the input component with an € adornment.
    const moneyFieldSlotProps = {
      input: {
        endAdornment: <InputAdornment position="end">€</InputAdornment>,
        inputComponent: CustomNumber
      }
    }

    // EMAIL: show a MailTo icon using an InputAdornment.
    const emailFieldSlotProps = {
      input: {
        endAdornment: (
          <InputAdornment position="end" sx={{ marginRight: '-10px' }}>
            <MailTo
              email={value}
              subject={defaultFieldProps.subject}
              body={defaultFieldProps.body}
              bcc={defaultFieldProps.bcc}
              caseEmailContext={caseEmailContext}
            />
          </InputAdornment>
        )
      }
    }

    // COPY ADDRESS: use a CopyAddressButton with an InputAdornment.
    const copyAddressFieldSlotProps = {
      input: {
        endAdornment: (
          <InputAdornment position="end" sx={{ marginRight: '-10px' }}>
            <CopyAddressButton prefix={defaultFieldProps.prefix} />
          </InputAdornment>
        )
      }
    }

    const copyableFieldSlotProps = {
      input: {
        endAdornment: (
          <InputAdornment position="end" sx={{ marginRight: '-10px' }}>
            <CopyButton value={value || ''} />
          </InputAdornment>
        )
      }
    }

    // EXTERNAL LINK: open the configured URL in a new tab via an OpenInNew icon.
    const externalLinkFieldSlotProps = {
      input: {
        endAdornment: externalUrl ? (
          <InputAdornment position="end" sx={{ marginRight: '-10px' }}>
            <IconButton
              size="small"
              aria-label="open external link"
              onClick={() => window.open(externalUrl, '_blank', 'noopener,noreferrer')}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined
      }
    }

    // PASSWORD: simply override the type attribute (keeping the logic intact).
    const passwordFieldProps = { type: visible ? 'text' : 'password' }

    if (!!mask)
      return (
        <TextField
          {...defaultFieldProps}
          {...props}
          onChange={undefined}
          inputRef={ref}
          slotProps={{
            input: {
              inputComponent: MaskedInputComponent,
              inputProps: {
                mask,
                lazy: !alwaysShowMask,
                placeholderChar: maskChar ?? '_',
                definitions: {
                  '9': /[0-9]/,
                  z: /[A-Za-z]/,
                  Z: /[A-Z]/
                },
                onAccept: (val: string) => {
                  if (onChange) {
                    const event = {
                      target: { value: val, name }
                    } as React.ChangeEvent<HTMLInputElement>
                    onChange(event)
                  }
                }
              }
            }
          }}
        />
      )
    // Render based on the type
    if (type === 'money') {
      return <TextField {...defaultFieldProps} slotProps={moneyFieldSlotProps} />
    }

    if (type === 'password') {
      return <TextField {...defaultFieldProps} {...passwordFieldProps} />
    }

    if (type === 'email') {
      return <TextField {...defaultFieldProps} slotProps={emailFieldSlotProps} />
    }

    if (type === 'copyAddress') {
      return <TextField {...defaultFieldProps} slotProps={copyAddressFieldSlotProps} />
    }

    if (type === 'copyable') {
      return <TextField {...defaultFieldProps} slotProps={copyableFieldSlotProps} />
    }

    if (type === 'externalLink') {
      return <TextField {...defaultFieldProps} slotProps={externalLinkFieldSlotProps} />
    }

    if (type === 'phone') {
      return (
        <Tooltip
          title={value ? formatPhoneNumberIntl(value) : ''}
          placement="top"
          slotProps={{
            tooltip: {
              style: {
                fontSize: '72px',
                letterSpacing: '0.1em',
                maxWidth: 'none',
                width: 'fit-content'
              }
            }
          }}
          disableInteractive
          disableTouchListener
        >
          <TextField
            inputRef={ref}
            {...defaultFieldProps}
            slotProps={{
              input: {
                inputComponent: PhoneNumberInput
              }
            }}
            sx={{
              '& .PhoneInput': { display: 'flex' },
              '& .PhoneInputInput': {
                border: 'none',
                fontFamily: 'fontFamily',
                font: 'inherit',
                color: 'currentColor'
              },
              '& .PhoneInputInput::focus-visible': {
                backgroundColor: 'lime'
              },
              '& .PhoneInput--focus input': {
                outline: 'none !important'
              }
            }}
          />
        </Tooltip>
      )
    }

    return (
      <TextField
        {...defaultFieldProps}
        {...(!error && helperTextColor
          ? {
              slotProps: {
                formHelperText: {
                  sx: {
                    color: helperTextColor
                  }
                }
              }
            }
          : {})}
      />
    )
  }
)
Input.displayName = 'Input'

export default Input
